import { queryClient } from "@bodhistreams/queries";
import { logClientEvent } from "@bodhistreams/utils";
import axios from "axios";
import { refreshToken } from "./authApi";

const axiosInstanceV1 = axios.create({
	baseURL: import.meta.env.DEV ? "/api/v1" : "https://api.bodhistreams.com",
	timeout: 100000,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
});

// Request interceptor — authentication handled via cookies
axiosInstanceV1.interceptors.request.use(
	(config) => {
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

let isRefreshing = false;
let failedQueue = [];

// While a logout is in progress we must NOT silently refresh the session.
// The server now revokes the refresh token on logout, so a late refresh gets a
// 401 rather than a new session — but the request still races the revocation,
// and a refresh that lands first would re-mint cookies and log the user back
// in. This latch keeps that window shut on the client side too.
let isLoggingOut = false;

/**
 * Toggle the logout latch. Set true at the very start of the logout flow so no
 * in-flight 401 can trigger a session-restoring refresh; the hard navigation
 * that follows logout resets this back to false on the fresh page load.
 * @param {boolean} value
 */
export const setLoggingOut = (value) => {
	isLoggingOut = value;
};

/**
 * Whether a logout is in progress.
 *
 * Anything that calls `refreshToken()` **outside** this interceptor must check
 * this first — the latch below only guards the 401-retry path. The WebSocket
 * close handlers learned that the hard way: logout clears the cookies, the
 * socket closes with TOKEN_EXPIRED, and an unguarded reconnect-refresh re-mints
 * the session and logs the user straight back in.
 * @returns {boolean}
 */
export const getLoggingOut = () => isLoggingOut;

const processQueue = (error, token = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});

	failedQueue = [];
};

// Response interceptor to handle token refresh
axiosInstanceV1.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const requestUrl = originalRequest?.url || "";

		// Never silently refresh for the public auth endpoints themselves
		// (login / register / refresh / logout / google / password-reset). A 401
		// there means "these credentials are invalid" — not "the access token
		// expired" — so refreshing would be wrong (and could re-login on logout).
		const isPublicAuthCall = requestUrl.includes("/public/auth/");

		// If error is 401 and we haven't tried to refresh token yet
		if (
			error.response?.status === 401 &&
			!originalRequest._retry &&
			!isLoggingOut &&
			!isPublicAuthCall
		) {
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then(() => {
						return axiosInstanceV1(originalRequest);
					})
					.catch((err) => {
						return Promise.reject(err);
					});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				// Call refresh with empty body — server reads refresh_token from cookie
				await refreshToken("", "interceptor");

				// Invalidate the auth identity query so all components get fresh auth state
				queryClient.invalidateQueries({ queryKey: ["auth", "me"] });

				processQueue(null);
				return axiosInstanceV1(originalRequest);
			} catch (refreshError) {
				processQueue(refreshError, null);

				const refreshStatus = refreshError?.response?.status;

				logClientEvent({
					kind: "auth_error",
					level: "error",
					status: refreshStatus || 401,
					method: (originalRequest?.method || "").toUpperCase(),
					url: originalRequest?.url || "",
					message: "token refresh failed",
					meta: {
						refresh_error: refreshError?.response?.data?.error || "",
						logging_out: isLoggingOut,
					},
				});

				// Only bounce to /login when the server actually rejected the
				// session (401/403). A 5xx means "couldn't check right now" —
				// e.g. the revocation store is briefly unreachable — and logging
				// everyone out over a transient backend blip is worse than
				// letting the caller see the error and retry.
				const sessionRejected = refreshStatus === 401 || refreshStatus === 403;

				// Redirect to login on refresh failure, but not on public pages
				const publicPages = [
					"/",
					"/pricing",
					"/about",
					"/brandbook",
					"/contact",
				];
				const currentPath = window.location.pathname;
				const isPublicPage = publicPages.some(
					(page) => currentPath === page || currentPath.startsWith(`${page}/`),
				);

				if (
					sessionRejected &&
					!isLoggingOut &&
					window.location.pathname !== "/login" &&
					!isPublicPage
				) {
					window.location.href = "/login";
				}
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		// Best-effort telemetry for any failed response (incl. network errors and
		// persistent 401s that fell through the refresh path above). De-duped &
		// batched inside logClientEvent; uses a raw fetch so it can't recurse here.
		const status = error.response?.status;
		logClientEvent({
			kind: status === 401 || status === 403 ? "auth_error" : "api_error",
			level: status >= 500 || status === undefined ? "error" : "warn",
			status: status ?? 0,
			method: (originalRequest?.method || "").toUpperCase(),
			url: originalRequest?.url || "",
			message:
				error.response?.data?.message || error.message || "request failed",
		});

		return Promise.reject(error);
	},
);

export default axiosInstanceV1;
